import {
  Component,
  effect,
  inject,
  OnDestroy,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Router } from "@angular/router";
import { secureShuffle } from "src/app/shared/utils/secure-random";
import { MobilitySearchStore } from "src/app/features/store/mobility-search-store";
import { ContactCard } from "src/app/shared/components/contact-card/contact-card";
import { Button } from "src/app/shared/components/utils/button/button";
import { Loader } from "src/app/shared/components/loader/loader";
import { FilterResultSection } from "src/app/shared/components/filter-result-section/filter-result-section";
import { MobilityContactCardInterface } from "src/app/core/interfaces/mobility-contact-card-interface";
import { LanguageFilter } from "src/app/shared/components/language-filter/language-filter";
import { TranslocoModule } from "@jsverse/transloco";
import { LanguageService } from "src/app/core/services/language-service";

@Component({
  selector: "app-mobility-result-page",
  imports: [
    ContactCard,
    Button,
    Loader,
    FilterResultSection,
    LanguageFilter,
    TranslocoModule,
  ],
  templateUrl: "./mobility-ref-result-page.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .filters-scrollbar {
        scrollbar-color: var(--color-vinciBlue-600) var(--color-vinciGrey-200);
        scrollbar-width: thin;
      }
    `,
  ],
})
export class MobilityRefResultPage implements OnDestroy {
  private readonly languageService = inject(LanguageService);
  mobilitySearchStore = inject(MobilitySearchStore);
  router = inject(Router);

  loading = signal<boolean>(false);
  resultsRandomlySorted = signal<MobilityContactCardInterface[]>([]);
  resultsDisplayed: number = 3;
  languagesAvailable = signal<string[]>([]);

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();

      untracked(async () => {
        this.loading.set(true);
        try {
          await this.mobilitySearchStore.loadMobilityRefFilters();
          this.mobilitySearchStore.updateSelectedFiltersTranslations();
          await this.mobilitySearchStore.loadMobilityRefContact();
          this.updateLanguagesAvailable();
        } catch (error) {
          console.error(
            "Erreur lors de la récupération des Référents Mobilités:",
            error
          );
        } finally {
          this.loading.set(false);
          this.resultsRandomlySorted.set(
            this.sortResults(this.mobilitySearchStore.results())
          );
        }
      });
    });
  }

  private updateLanguagesAvailable(): void {
    this.languagesAvailable.set([]);
    for (const contact of this.mobilitySearchStore.results()) {
      (contact.languages ?? []).forEach((lang) => {
        if (!this.languagesAvailable().includes(lang)) {
          this.languagesAvailable.set(
            [...this.languagesAvailable(), lang].sort((a, b) =>
              a.localeCompare(b)
            )
          );
        }
      });
    }
  }

  redirectToResearch(): void {
    this.router.navigate(["/mobility/referents-search"]);
  }

  sortResults(
    mobilityRefs: MobilityContactCardInterface[]
  ): MobilityContactCardInterface[] {
    return secureShuffle(mobilityRefs).slice(0, this.resultsDisplayed);
  }

  updateResults(languagesSelected: string[]): void {
    if (languagesSelected.length === 0) {
      this.resultsRandomlySorted.set(
        this.sortResults(this.mobilitySearchStore.results())
      );
    } else {
      const filteredResults = this.mobilitySearchStore
        .results()
        .filter((contact) =>
          languagesSelected.every((lang) =>
            (contact.languages ?? []).includes(lang)
          )
        );
      this.resultsRandomlySorted.set(this.sortResults(filteredResults));
    }
  }

  ngOnDestroy() {
    this.mobilitySearchStore.setResults([]);
  }
}
